(function (global) {
  const PUBLIC_PRODUCT_FIELDS = Object.freeze([
    'title',
    'body',
    'product_type',
    'tag',
    'variants.title',
    'vendor',
  ]);
  const DISPLAY_PARAM = 'fwd_q';
  const SCOPE_PARAM = 'fwd_search_scope';
  const SCOPE_VERSION = 'public-fields-v3';
  const TEMPLATE_TOKEN = '__FWD_QUERY__';
  const MAX_QUERY_CHARACTERS = 120;
  const MAX_QUERY_TOKENS = 20;
  const MAX_ENCODED_STOREFRONT_QUERY_LENGTH = 4800;
  const SAFE_REJECT_QUERY = '(title:"__fwd_query_budget_v1__" AND NOT title:"__fwd_query_budget_v1__")';

  function tokenize(query) {
    const tokens = [];
    let value = '';
    let quoted = false;
    let tokenWasQuoted = false;
    let escaped = false;

    const pushToken = () => {
      if (!value) return;
      tokens.push({ value, quoted: tokenWasQuoted });
      value = '';
      tokenWasQuoted = false;
    };

    for (const character of String(query || '').trim()) {
      if (escaped) {
        value += character;
        escaped = false;
      } else if (character === '\\') {
        escaped = true;
      } else if (character === '"') {
        quoted = !quoted;
        tokenWasQuoted = true;
      } else if (/\s/u.test(character) && !quoted) {
        pushToken();
      } else {
        value += character;
      }
    }

    if (escaped) value += '\\';
    pushToken();
    return tokens;
  }

  function quoteToken(value) {
    return `"${String(value).replace(/\\/gu, '\\\\').replace(/"/gu, '\\"')}"`;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/gu, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[character]);
  }

  function highlightMarkup(text, query) {
    const terms = tokenize(query)
      .map((token) => token.value)
      .filter(Boolean)
      .sort((left, right) => right.length - left.length)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&'));
    if (!terms.length) return escapeHtml(text);

    const matcher = new RegExp(terms.join('|'), 'giu');
    let cursor = 0;
    let markup = '';
    for (const match of String(text).matchAll(matcher)) {
      markup += escapeHtml(String(text).slice(cursor, match.index));
      markup += `<strong class="highlight">${escapeHtml(match[0])}</strong>`;
      cursor = match.index + match[0].length;
    }
    return markup + escapeHtml(String(text).slice(cursor));
  }

  function storefrontToken(token, isLast, prefixLast) {
    const needsQuotes = token.quoted
      || /[:()*\[\]{}]/u.test(token.value)
      || /^[-+]/u.test(token.value)
      || /^(?:AND|OR|NOT)$/iu.test(token.value);
    const value = needsQuotes
      ? quoteToken(token.value)
      : token.value.replace(/([\\:()])/gu, '\\$1');

    // Shopify's field-scoped tokenizer returns no results for CJK terms with a trailing wildcard.
    const supportsPrefix = /^[\x20-\x7e]+$/u.test(token.value);
    return prefixLast && isLast && !needsQuotes && supportsPrefix ? `${value}*` : value;
  }

  function toPositivePublicQuery(query, { prefixLast = true } = {}) {
    const tokens = tokenize(query);
    return tokens.map((token, index) => {
      const value = storefrontToken(token, index === tokens.length - 1, prefixLast);
      const fields = PUBLIC_PRODUCT_FIELDS.map((field) => `${field}:${value}`);
      // Denim model labels also use W505; keep numeric matching in public title fields.
      if (!token.quoted && /^\d+$/u.test(token.value)) {
        const model = storefrontToken({ value: `w${token.value}`, quoted: false }, index === tokens.length - 1, prefixLast);
        fields.push(`title:${model}`, `variants.title:${model}`);
      }
      // Preserve the existing Korean women's search against English public product copy.
      if (!token.quoted && token.value === '여성') {
        const women = storefrontToken({ value: 'women', quoted: false }, index === tokens.length - 1, prefixLast);
        fields.push(...PUBLIC_PRODUCT_FIELDS.map((field) => `${field}:${women}`));
      }
      return `(${fields.join(' OR ')})`;
    }).join(' AND ');
  }

  function toStorefrontQuery(query, { prefixLast = true } = {}) {
    const rawInput = String(query || '').trim();
    const tokens = tokenize(query);
    if (!tokens.length) return '';

    const storefrontQuery = toPositivePublicQuery(query, { prefixLast });

    if (
      rawInput.length > MAX_QUERY_CHARACTERS
      || tokens.length > MAX_QUERY_TOKENS
      || encodeURIComponent(storefrontQuery).length > MAX_ENCODED_STOREFRONT_QUERY_LENGTH
    ) {
      return SAFE_REJECT_QUERY;
    }
    return storefrontQuery;
  }

  function isBudgetRejectedQuery(query) {
    return query === SAFE_REJECT_QUERY;
  }

  function isGeneratedStorefrontQuery(query) {
    return query.startsWith('(title:') || query.includes(') AND NOT (variants.sku:') || isBudgetRejectedQuery(query);
  }

  function readDisplayQuery(search = global.location?.search || '') {
    const params = search instanceof URLSearchParams ? search : new URLSearchParams(search);
    const displayQuery = (params.get(DISPLAY_PARAM) || '').trim();
    if (displayQuery) return displayQuery;

    const storefrontQuery = (params.get('q') || '').trim();
    if (isGeneratedStorefrontQuery(storefrontQuery)) return '';
    return storefrontQuery;
  }

  function applyStorefrontContract(params, query, { prefixLast = true } = {}) {
    const rawQuery = String(query || '').trim();
    params.set(DISPLAY_PARAM, rawQuery);
    params.set('q', toStorefrontQuery(rawQuery, { prefixLast }));
    params.set(SCOPE_PARAM, SCOPE_VERSION);
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'show');
    if (!params.has('filter.v.availability') && params.get('fwd_inventory') !== 'all') {
      params.set('filter.v.availability', '1');
    }
    return params;
  }

  function buildSearchUrl(query, options = {}) {
    const base = options.base || global.location?.origin || 'https://example.invalid';
    const url = new URL(options.path || global.fwdSearchConfig?.searchUrl || '/search', base);
    applyStorefrontContract(url.searchParams, query, options);
    if (options.unavailableProducts) {
      url.searchParams.set('options[unavailable_products]', options.unavailableProducts);
    }
    if (options.sectionId) url.searchParams.set('section_id', options.sectionId);
    return options.absolute ? url.toString() : `${url.pathname}${url.search}`;
  }

  function configurePredictiveUrl(url, query) {
    url.searchParams.set('q', String(query || '').trim());
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', PUBLIC_PRODUCT_FIELDS.join(','));
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    return url;
  }

  function cacheKey(query, surface = 'search') {
    return `${SCOPE_VERSION}:in-stock-default:${surface}:${String(query || '').trim().toLocaleLowerCase()}`;
  }

  function ensureHiddenInput(form, name, value) {
    let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!input) {
      input = global.document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    return input;
  }

  function prepareSearchForm(form, query) {
    if (!form) return;
    const input = form.querySelector('[data-fwd-search-input], input[type="search"]');
    const rawQuery = String(query ?? input?.value ?? '').trim();
    if (input) {
      input.name = DISPLAY_PARAM;
      input.value = query == null ? input.value : rawQuery;
    }
    ensureHiddenInput(form, 'q', toStorefrontQuery(rawQuery));
    ensureHiddenInput(form, SCOPE_PARAM, SCOPE_VERSION);
    ensureHiddenInput(form, 'type', 'product');
    ensureHiddenInput(form, 'options[prefix]', 'last');
    ensureHiddenInput(form, 'options[unavailable_products]', 'show');
  }

  function hydrate(root = global.document, query) {
    if (!root) return;
    const rawQuery = String(query ?? readDisplayQuery()).trim();
    root.querySelectorAll('[data-fwd-search-form]').forEach((form) => {
      prepareSearchForm(form, rawQuery || null);
    });

    if (!rawQuery) return;
    root.querySelectorAll('[data-fwd-search-display-query]').forEach((element) => {
      element.textContent = rawQuery;
    });
    root.querySelectorAll('[data-fwd-search-copy-template]').forEach((element) => {
      element.textContent = element.dataset.fwdSearchCopyTemplate.replace(TEMPLATE_TOKEN, rawQuery);
    });
    root.querySelectorAll('[data-fwd-search-highlight]').forEach((element) => {
      element.innerHTML = highlightMarkup(element.textContent, rawQuery);
    });
    root.querySelectorAll('[data-fwd-search-reset-url], .fwd-collection-empty__link').forEach((element) => {
      const url = new URL(element.href, global.location?.origin || 'https://example.invalid');
      url.searchParams.delete('filter.v.availability');
      url.searchParams.set('fwd_inventory', 'all');
      applyStorefrontContract(url.searchParams, rawQuery);
      element.href = `${url.pathname}${url.search}`;
    });

    const scopedQuery = new URLSearchParams(global.location?.search || '').get('q');
    if (root === global.document && scopedQuery && global.document?.title?.includes(scopedQuery)) {
      global.document.title = global.document.title.replace(scopedQuery, rawQuery);
    }
  }

  function redirectUnscopedSearch() {
    if (!global.location || !global.fwdSearchConfig?.searchUrl) return false;
    const currentUrl = new URL(global.location.href);
    const searchPath = new URL(global.fwdSearchConfig.searchUrl, currentUrl.origin).pathname;
    if (currentUrl.pathname !== searchPath) return false;
    if (currentUrl.searchParams.get('type') && currentUrl.searchParams.get('type') !== 'product') return false;
    const displayQuery = (currentUrl.searchParams.get(DISPLAY_PARAM) || '').trim();
    const currentQuery = (currentUrl.searchParams.get('q') || '').trim();
    if (!displayQuery && isGeneratedStorefrontQuery(currentQuery)) {
      currentUrl.searchParams.delete('q');
      currentUrl.searchParams.delete(SCOPE_PARAM);
      currentUrl.searchParams.delete('page');
      global.location.replace(currentUrl.toString());
      return true;
    }
    const expectedQuery = toStorefrontQuery(displayQuery);
    if (
      currentUrl.searchParams.get(SCOPE_PARAM) === SCOPE_VERSION
      && displayQuery
      && currentUrl.searchParams.get('q') === expectedQuery
      && currentUrl.searchParams.get('type') === 'product'
      && currentUrl.searchParams.get('options[prefix]') === 'last'
      && currentUrl.searchParams.get('options[unavailable_products]') === 'show'
    ) {
      return false;
    }

    const rawQuery = displayQuery || currentQuery;
    if (!rawQuery) return false;
    applyStorefrontContract(currentUrl.searchParams, rawQuery);
    currentUrl.searchParams.delete('page');
    global.location.replace(currentUrl.toString());
    return true;
  }

  const api = Object.freeze({
    PUBLIC_PRODUCT_FIELDS,
    DISPLAY_PARAM,
    SCOPE_PARAM,
    SCOPE_VERSION,
    MAX_QUERY_CHARACTERS,
    MAX_QUERY_TOKENS,
    MAX_ENCODED_STOREFRONT_QUERY_LENGTH,
    SAFE_REJECT_QUERY,
    tokenize,
    toPositivePublicQuery,
    toStorefrontQuery,
    isBudgetRejectedQuery,
    readDisplayQuery,
    applyStorefrontContract,
    buildSearchUrl,
    configurePredictiveUrl,
    cacheKey,
    prepareSearchForm,
    hydrate,
    redirectUnscopedSearch,
  });

  global.FwdSearchQuery = api;

  if (global.document) {
    global.document.addEventListener('input', (event) => {
      if (event.target.matches('[data-fwd-search-input]')) prepareSearchForm(event.target.form);
    });
    global.document.addEventListener('submit', (event) => {
      if (event.target.matches('[data-fwd-search-form]')) prepareSearchForm(event.target);
    });
    global.document.addEventListener('DOMContentLoaded', () => {
      if (!redirectUnscopedSearch()) hydrate();
    });
    global.addEventListener?.('popstate', () => hydrate());
  }
})(typeof window === 'undefined' ? globalThis : window);
